// Shared approve/reject state machine for a single unified memory row —
// used by both the standalone /agents/memories list (MemoryListRow) and the
// PA chat transcript's inline save_memory card (SaveMemoryToolCard). Both
// surfaces show the same three states off the same two mutations; this
// factors out the mutation wiring + inline-error handling so a fix to one
// (e.g. an error-message improvement) doesn't have to be repeated in the
// other.
import type { MemoryStatus } from '@cortex/types';
import { useState } from 'react';

import { isMemoryPendingReview, useApproveMemory, useRejectMemory } from './agent-memory';

export function useMemoryApprovalActions() {
  const approve = useApproveMemory();
  const reject = useRejectMemory();
  const [error, setError] = useState<string | null>(null);

  async function runMutation(p: Promise<unknown>) {
    setError(null);
    try {
      await p;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  }

  return { approve, reject, error, setError, runMutation };
}

/** Derives the three mutually-exclusive lifecycle flags a memory row renders around. */
export function memoryLifecycle(
  status: MemoryStatus,
  archivedAt: string | Date | null | undefined,
) {
  const archived = archivedAt != null;
  return {
    archived,
    pending: isMemoryPendingReview(status) && !archived,
    approved: status === 'APPROVED' && !archived,
    rejected: status === 'REJECTED',
  };
}
